import { useEffect, useState } from 'react';
import {
  doc,
  getDoc,
  getDocs,
  query,
  where,
  collection,
} from 'firebase/firestore';
import { db } from '@/core/firebase';
import { Button, Panel, Badge } from '@/components/ui';
import type { User, WorkoutTemplate } from '@/types';
import { startAdHocWorkoutSession } from '@/lib/startAdHocWorkoutSession';

interface QuickStartModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSessionCreated: (sessionId: string) => void;
  userId: string;
  trainerId: string;
}

export function QuickStartModal({
  isOpen,
  onClose,
  onSessionCreated,
  userId,
  trainerId,
}: QuickStartModalProps) {
  const [rotationTemplates, setRotationTemplates] = useState<WorkoutTemplate[]>([]);
  const [otherTemplates, setOtherTemplates] = useState<WorkoutTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Fetch available templates when modal opens
  // Shows rotation templates first, in rotation order, then other client-owned templates.
  useEffect(() => {
    if (!isOpen || !trainerId) return;

    const fetchTemplates = async () => {
      setLoading(true);
      setError(null);
      try {
        const [userSnap, templateSnapshot] = await Promise.all([
          getDoc(doc(db, 'users', userId)),
          getDocs(
            query(
              collection(db, 'workoutTemplates'),
              where('clientId', '==', userId)
            )
          ),
        ]);

        const userData = userSnap.exists() ? (userSnap.data() as User) : null;
        const rotationTemplateIds = userData?.trainingPlan?.rotationTemplates || [];
        const dailyRotationTemplateIds = userData?.trainingPlan?.dailyRotationTemplateIds || [];

        const allDocs = templateSnapshot.docs.map(
          (docSnap) => ({ id: docSnap.id, ...docSnap.data() } as WorkoutTemplate)
        );

        const trainerTemplates = allDocs.filter((t) => t.trainerId === trainerId);
        const templateById = new Map(trainerTemplates.map((template) => [template.id, template]));

        const orderedRotationTemplates = rotationTemplateIds
          .map((id) => templateById.get(id))
          .filter((template): template is WorkoutTemplate => Boolean(template));

        const rotationTemplateIdSet = new Set(orderedRotationTemplates.map((template) => template.id));
        const dailyRotationTemplateIdSet = new Set(dailyRotationTemplateIds);
        const remainingTemplates = trainerTemplates
          .filter((template) =>
            !rotationTemplateIdSet.has(template.id) &&
            !dailyRotationTemplateIdSet.has(template.id)
          )
          .sort((a, b) => a.name.localeCompare(b.name));

        setRotationTemplates(orderedRotationTemplates);
        setOtherTemplates(remainingTemplates);
      } catch (err) {
        console.error('Failed to fetch templates:', err);
        setError('Unable to load workouts. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchTemplates();
  }, [isOpen, trainerId, userId]);

  const startQuickSession = async (template: WorkoutTemplate) => {
    setStarting(template.id);
    setError(null);

    try {
      const sessionRef = await startAdHocWorkoutSession({
        template,
        clientId: userId,
        trainerId,
        sessionType: 'ad_hoc',
      });

      onSessionCreated(sessionRef.id);
    } catch (err: any) {
      console.error('Failed to start quick session:', err);
      setError(err.message || 'Unable to start workout. Please try again.');
      setStarting(null);
    }
  };

  return (
    <Panel
      isOpen={isOpen}
      onClose={onClose}
      title="Quick Start Workout"
      variant="modal"
      size="md"
    >
      <div className="space-y-4">
        {/* Description */}
        <p className="text-sm text-gray-600 dark:text-slate-400">
          Choose a workout to start immediately. This creates an unscheduled session
          you can do anytime.
        </p>

        {/* Error Message */}
        {error && (
          <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 text-sm">
            {error}
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-8">
            <div className="flex items-center gap-3">
              <svg
                className="animate-spin h-5 w-5 text-primary-500"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              <span className="text-gray-600 dark:text-slate-400">Loading workouts...</span>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!loading && rotationTemplates.length === 0 && otherTemplates.length === 0 && (
          <div className="text-center py-8">
            <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-gray-100 dark:bg-white/10 flex items-center justify-center">
              <svg
                className="w-6 h-6 text-gray-400 dark:text-slate-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                />
              </svg>
            </div>
            <p className="text-gray-600 dark:text-slate-400 text-sm">
              No workout templates available yet.
            </p>
            <p className="text-gray-500 dark:text-slate-500 text-xs mt-1">
              Your trainer will create workouts for you.
            </p>
          </div>
        )}

        {/* Template List */}
        {!loading && (rotationTemplates.length > 0 || otherTemplates.length > 0) && (
          <div className="space-y-4 max-h-[50vh] overflow-y-auto">
            {rotationTemplates.length > 0 && (
              <div className="space-y-2">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-primary-600 dark:text-primary-400">
                    Rotation
                  </p>
                  <p className="text-sm text-gray-600 dark:text-slate-400">
                    Your current trainer rotation, in order.
                  </p>
                </div>
                {rotationTemplates.map((template, index) => (
                  <button
                    key={template.id}
                    onClick={() => startQuickSession(template)}
                    disabled={starting !== null}
                    className="w-full text-left p-4 rounded-xl border border-primary-200 dark:border-primary-500/30 bg-primary-50 dark:bg-primary-500/10 hover:bg-primary-100 dark:hover:bg-primary-500/20 hover:border-primary-300 dark:hover:border-primary-500/40 transition disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-medium text-gray-900 dark:text-white truncate">
                            {index + 1}. {template.name}
                          </p>
                          <Badge variant="primary" size="sm">Rotation</Badge>
                        </div>
                        <p className="text-sm text-gray-600 dark:text-slate-400 mt-0.5">
                          {template.items.length} exercise{template.items.length !== 1 ? 's' : ''}
                        </p>
                        {template.notes && (
                          <p className="text-xs text-gray-500 dark:text-slate-500 mt-1 line-clamp-1">
                            {template.notes}
                          </p>
                        )}
                      </div>
                      <div className="flex-shrink-0">
                        {starting === template.id ? (
                          <Badge variant="primary" size="sm">
                            <svg
                              className="animate-spin h-3 w-3 mr-1"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              />
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              />
                            </svg>
                            Starting...
                          </Badge>
                        ) : (
                          <Badge variant="default" size="sm">
                            Start →
                          </Badge>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {otherTemplates.length > 0 && (
              <div className="space-y-2">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400">
                    Other workouts
                  </p>
                  <p className="text-sm text-gray-600 dark:text-slate-400">
                    Additional client templates outside your active rotation.
                  </p>
                </div>
                {otherTemplates.map((template) => (
                  <button
                    key={template.id}
                    onClick={() => startQuickSession(template)}
                    disabled={starting !== null}
                    className="w-full text-left p-4 rounded-xl border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 hover:bg-primary-50 dark:hover:bg-primary-500/10 hover:border-primary-300 dark:hover:border-primary-500/30 transition disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 dark:text-white truncate">
                          {template.name}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-slate-400 mt-0.5">
                          {template.items.length} exercise{template.items.length !== 1 ? 's' : ''}
                        </p>
                        {template.notes && (
                          <p className="text-xs text-gray-500 dark:text-slate-500 mt-1 line-clamp-1">
                            {template.notes}
                          </p>
                        )}
                      </div>
                      <div className="flex-shrink-0">
                        {starting === template.id ? (
                          <Badge variant="primary" size="sm">
                            <svg
                              className="animate-spin h-3 w-3 mr-1"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              />
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              />
                            </svg>
                            Starting...
                          </Badge>
                        ) : (
                          <Badge variant="default" size="sm">
                            Start →
                          </Badge>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Cancel Button */}
        <div className="pt-4 border-t border-gray-200 dark:border-white/10">
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={starting !== null}
            className="w-full"
          >
            Cancel
          </Button>
        </div>
      </div>
    </Panel>
  );
}
